import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate, formatSEK } from '../../lib/format-utils';
import { getBillingReport, getProjects, generateInvoice } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/errors';
// Hours are accumulated below (per-project totals summed into per-customer
// ones), so every hours figure on this page goes through one rounding at the
// display boundary. Money does not — it is server-computed and printed as
// given (formatSEK).
import { formatHours } from './invoice-utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
    FileSpreadsheet, Clock, DollarSign,
    FolderKanban, Receipt, Building2, ArrowLeft, Send, Loader2,
} from 'lucide-react';
import MonthNavigator from '../../components/month-navigator';
import { SummaryCard, SummaryCardRow } from '../../components/summary-cards';

// Bucket key for projects with no client_id — grouped explicitly rather
// than hidden, so an admin can see unassigned billable work exists without
// it being mistaken for a real customer (Task 21.10, PRD 10.6/10.13).
const NO_CLIENT_KEY = '__no_client__';

export default function BillingReportPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [reportData, setReportData] = useState(null);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState('');

    // Customer-wise view (Task 21.10) — a tab alongside the existing
    // project-wise view, not a replacement for it (admins already rely on
    // the project view). selectedClientKey drives the drill-down within the
    // customer tab; it is cleared whenever the underlying report changes so
    // a stale drill-down never survives a month/project-filter switch.
    //
    // IT OPENS ON THE CUSTOMER TAB (Task 22.8). "Who do we invoice this
    // month, and for how much" is the question this page is opened to
    // answer, and the answer — with its Generate-invoice action — was one
    // click behind the project view, far enough out of sight that the
    // product owner did not know it existed. The project view is unchanged
    // and still one click away; only which of the two greets you moved.
    const [view, setView] = useState('customer');
    const [selectedClientKey, setSelectedClientKey] = useState(null);
    const [generatingKey, setGeneratingKey] = useState(null);
    const [generatedInvoice, setGeneratedInvoice] = useState(null);

    useEffect(() => {
        fetchProjects();
    }, []);

    useEffect(() => {
        setSelectedClientKey(null);
        fetchReport();
    }, [month, year, selectedProject]);

    const fetchProjects = async () => {
        try {
            const res = await getProjects({ all: true });
            // Filter to only billable projects
            setProjects(res.data.filter(p => p.billable));
        } catch (error) {
            toast.error(t('billing.failed_load_projects'));
        }
    };

    const fetchReport = async () => {
        setLoading(true);
        try {
            const res = await getBillingReport(month, year, selectedProject || undefined);
            setReportData(res.data);
        } catch (error) {
            toast.error(t('billing.failed_load'));
        } finally {
            setLoading(false);
        }
    };

    const navigateMonth = (direction) => {
        if (direction === 'prev') {
            if (month === 1) {
                setMonth(12);
                setYear(year - 1);
            } else {
                setMonth(month - 1);
            }
        } else {
            if (month === 12) {
                setMonth(1);
                setYear(year + 1);
            } else {
                setMonth(month + 1);
            }
        }
    };

    // Group the SAME per-project reports the project-wise tab already shows
    // into one row per customer — no second fetch, no separate backend
    // shape. Only customers with something billable this period are kept:
    // the point of this view is that out of twenty or thirty customers,
    // only a handful need billing in any given month (PRD 10.6/10.13).
    //
    // Two DIFFERENT null cases are deliberately NOT collapsed into one
    // bucket (CodeRabbit finding on Task 21.10):
    //   - no client_id at all → genuinely unassigned work, bucketed under
    //     NO_CLIENT_KEY, invoicing correctly disabled (nothing to bill).
    //   - client_id present but client_name did not resolve (deleted
    //     client, cross-tenant leak, any other resolution failure) → a
    //     BROKEN LINK, not an unassigned project. This is kept in its own
    //     per-client_id group (so it never silently merges with unrelated
    //     unassigned work or with other broken links), flagged visibly, and
    //     invoicing is disabled: generation needs a real client record to
    //     snapshot onto the invoice, and a client_id that doesn't resolve
    //     cannot supply one. The billable amount still counts in the
    //     summary cards above (that data is real), it just cannot be
    //     invoiced from here until the underlying client link is fixed.
    const customerGroups = useMemo(() => {
        const reports = reportData?.reports || [];
        const groups = new Map();
        for (const report of reports) {
            const key = report.client_id || NO_CLIENT_KEY;
            if (!groups.has(key)) {
                const unresolved = !!report.client_id && !report.client_name;
                groups.set(key, {
                    key,
                    clientId: report.client_id || null,
                    unresolved,
                    clientName: !report.client_id
                        ? t('billing.customers.no_client')
                        : (report.client_name
                            || t('billing.customers.unresolved_client', { id: report.client_id.slice(0, 8) })),
                    projectCount: 0,
                    totalHours: 0,
                    totalExpenses: 0,
                    totalAmount: 0,
                    reports: [],
                });
            }
            const group = groups.get(key);
            group.projectCount += 1;
            group.totalHours += report.total_hours || 0;
            // Expenses and mileage are already inside total_amount — this is
            // the same money shown separately, not an extra column to add to
            // the total (Task 22.8). Summed from the SAME per-project reports
            // the amount is summed from, so the row cannot disagree with
            // itself.
            group.totalExpenses += report.total_expenses || 0;
            group.totalAmount += report.total_amount || 0;
            group.reports.push(report);
        }
        return Array.from(groups.values())
            .filter((group) => group.totalAmount > 0)
            .sort((a, b) => b.totalAmount - a.totalAmount);
    }, [reportData, t]);

    const selectedGroup = customerGroups.find((g) => g.key === selectedClientKey) || null;

    const handleGenerateInvoice = async (group) => {
        // Never reachable via the UI (the button is hidden in both cases),
        // but guarded here too:
        //   - no client_id → nothing to bill an invoice to.
        //   - unresolved client_id → generation needs a real client record
        //     to snapshot onto the invoice; a broken link cannot supply one.
        if (!group.clientId || group.unresolved) return;
        // Generation bills the client's WHOLE month across every one of their
        // projects, but a project filter narrows what this screen shows to one.
        // Pressing it under a filter would raise an invoice covering more than
        // the admin can see — the figures they just read would not be the
        // figures on the document. The buttons are hidden in that state; this
        // is the boundary rather than the affordance.
        if (selectedProject) return;

        setGeneratingKey(group.key);
        try {
            const res = await generateInvoice({ client_id: group.clientId, year, month });
            setGeneratedInvoice({ clientName: group.clientName, invoice: res.data });
            toast.success(t('billing.customers.invoice_generated_toast', { client: group.clientName }));
        } catch (error) {
            const detail = error?.response?.data?.detail;
            const message = getApiErrorMessage(error, t('billing.customers.failed_generate_invoice'));
            // "Nothing to invoice" is an ordinary outcome (already fully
            // billed, or nothing billable yet) — not a scary error toast.
            if (detail === 'error.invoices.nothing_to_invoice') {
                toast.info(message);
            } else {
                toast.error(message);
            }
        } finally {
            setGeneratingKey(null);
        }
    };

    // Shared by the project-wise tab AND the customer drill-down — the
    // product owner was explicit that drilling into a customer should reuse
    // the existing project-by-project breakdown, not a second one.
    const renderProjectReports = (reports) => (
        <div className="space-y-6">
            {reports.map((report) => (
                <Card key={report.project_id} className="border border-border shadow-none" data-testid={`billing-project-${report.project_id}`}>
                    <CardContent className="p-0">
                        {/* Project Header */}
                        <div className="p-4 border-b border-border bg-muted/30">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h3 className="font-heading text-lg font-bold">{report.project_name}</h3>
                                    <p className="text-sm text-muted-foreground">
                                        {t('billing.rate', { rate: report.hourly_rate?.toLocaleString() })}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="font-mono text-xl font-bold text-green-600">
                                        {formatSEK(report.total_amount)}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        {t('billing.hours_logged', { hours: formatHours(report.total_hours) })}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Entries Table */}
                        {report.entries?.length > 0 && (
                            <>
                                <div className="px-4 py-2 bg-muted/20 border-b border-border">
                                    <h4 className="text-sm font-medium uppercase tracking-widest">{t('billing.time_entries')}</h4>
                                </div>
                                <div className="overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>{t('billing.date')}</th>
                                            <th>{t('billing.employee')}</th>
                                            <th>{t('billing.activity')}</th>
                                            <th className="text-right">{t('billing.hours')}</th>
                                            <th className="text-right">{t('billing.amount')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.entries?.map((entry, idx) => (
                                            <tr key={idx}>
                                                <td className="font-mono text-sm">{formatDate(entry.date)}</td>
                                                <td>{entry.user_name}</td>
                                                <td className="text-muted-foreground">{entry.activity || '-'}</td>
                                                <td className="text-right font-mono">{formatHours(entry.hours)}h</td>
                                                <td className="text-right font-mono font-medium">
                                                    {formatSEK(entry.amount)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                </div>
                            </>
                        )}

                        {/* Expenses Table */}
                        {report.expenses?.length > 0 && (
                            <>
                                <div className="px-4 py-2 bg-amber-50 border-b border-amber-200">
                                    <h4 className="text-sm font-medium uppercase tracking-widest text-amber-700">{t('billing.billable_expenses')}</h4>
                                </div>
                                <div className="overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>{t('billing.date')}</th>
                                            <th>{t('billing.employee')}</th>
                                            <th>{t('billing.type')}</th>
                                            <th>{t('billing.description')}</th>
                                            <th className="text-right">{t('billing.amount')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.expenses?.map((expense, idx) => (
                                            <tr key={idx}>
                                                <td className="font-mono text-sm">{formatDate(expense.date)}</td>
                                                <td>{expense.user_name}</td>
                                                <td>{expense.expense_type}</td>
                                                <td className="text-muted-foreground">{expense.description || '-'}</td>
                                                <td className="text-right font-mono font-medium">
                                                    {formatSEK(expense.amount)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                </div>
                            </>
                        )}

                        {/* Project Footer with Totals */}
                        <div className="p-4 bg-muted/30 border-t border-border">
                            <div className="flex justify-between items-center">
                                <span className="font-bold">{t('billing.project_total')}</span>
                                <div className="text-right">
                                    <p className="text-sm text-muted-foreground">
                                        {formatHours(report.total_hours)}h × {formatSEK(report.hourly_rate)}
                                        {report.total_expenses > 0 && (
                                            <span> + {formatSEK(report.total_expenses)} expenses</span>
                                        )}
                                    </p>
                                    <p className="font-mono text-xl font-bold text-green-600">
                                        {formatSEK(report.total_amount)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    );

    if (loading && !reportData) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="billing-report-page">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">{t('billing.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('billing.subtitle')}</p>
                </div>
            </div>

            {/* Month navigation — the shared navigator (Task 22.6). This page
                was the reference shape the component was extracted from, so
                adopting it changes nothing here beyond the 44px touch floor. */}
            <MonthNavigator
                year={year}
                month={month}
                onPrev={() => navigateMonth('prev')}
                onNext={() => navigateMonth('next')}
            />

            {/* Project Filter */}
            <div className="flex items-center gap-4">
                <label className="text-sm uppercase tracking-widest text-muted-foreground">{t('billing.filter_project')}</label>
                <select
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="h-10 px-3 border border-input bg-background text-sm min-w-[200px]"
                    data-testid="project-filter-select"
                >
                    <option value="">{t('billing.all_billable')}</option>
                    {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
                {selectedProject && (
                    <p className="text-xs text-muted-foreground max-w-md" data-testid="filter-hides-generate-hint">
                        {t('billing.customers.filter_hides_generate')}
                    </p>
                )}
            </div>

            {/* Summary Cards — the shared card row (Task 22.7). This page is
                where the shape came from, so it renders identically. */}
            {reportData && (
                <SummaryCardRow>
                    <SummaryCard
                        icon={FolderKanban}
                        label={t('billing.projects')}
                        value={reportData.reports?.length || 0}
                    />
                    <SummaryCard
                        icon={Clock}
                        label={t('billing.total_hours')}
                        value={`${formatHours(reportData.summary?.total_hours)}h`}
                    />
                    <SummaryCard
                        icon={Receipt}
                        label={t('billing.expenses')}
                        value={formatSEK(reportData.summary?.total_expenses || 0)}
                    />
                    <SummaryCard
                        icon={DollarSign}
                        label={t('billing.total_amount')}
                        value={formatSEK(reportData.summary?.total_amount || 0)}
                        variant="money"
                    />
                </SummaryCardRow>
            )}

            {/* Report Details — project-wise (existing) and customer-wise (Task 21.10) */}
            {loading ? (
                <div className="flex items-center justify-center h-32">
                    <div className="w-6 h-6 border-2 border-foreground border-t-transparent animate-spin"></div>
                </div>
            ) : !reportData?.reports?.length ? (
                <Card className="border border-border shadow-none">
                    <CardContent className="p-8 text-center">
                        <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 opacity-30" />
                        <p className="text-muted-foreground">{t('billing.no_data')}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                            {t('billing.no_data_hint')}
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <Tabs
                    value={view}
                    onValueChange={(v) => { setView(v); setSelectedClientKey(null); }}
                >
                    {/* Customer first because the page opens on it (Task 22.8).
                        Both triggers keep their original data-testid. */}
                    <TabsList>
                        <TabsTrigger value="customer" data-testid="billing-tab-customer">
                            {t('billing.customers.tab_customers')}
                        </TabsTrigger>
                        <TabsTrigger value="project" data-testid="billing-tab-project">
                            {t('billing.customers.tab_projects')}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="project" className="mt-4">
                        {renderProjectReports(reportData.reports)}
                    </TabsContent>

                    <TabsContent value="customer" className="mt-4">
                        {selectedGroup ? (
                            <div className="space-y-4">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSelectedClientKey(null)}
                                    className="uppercase tracking-wider text-xs"
                                    data-testid="back-to-customers-btn"
                                >
                                    <ArrowLeft className="w-4 h-4 mr-1" />
                                    {t('billing.customers.back_to_customers')}
                                </Button>
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border border-border p-4 bg-background">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-heading text-xl font-bold">{selectedGroup.clientName}</h3>
                                            {selectedGroup.unresolved && (
                                                <Badge variant="destructive">{t('billing.customers.unresolved_badge')}</Badge>
                                            )}
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {t('billing.hours_logged', { hours: formatHours(selectedGroup.totalHours) })}
                                            {' · '}
                                            {formatSEK(selectedGroup.totalAmount)}
                                        </p>
                                        {selectedGroup.unresolved && (
                                            <p className="text-xs text-destructive mt-1 max-w-md">
                                                {t('billing.customers.unresolved_hint')}
                                            </p>
                                        )}
                                    </div>
                                    {selectedGroup.clientId && !selectedGroup.unresolved && !selectedProject && (
                                        <Button
                                            onClick={() => handleGenerateInvoice(selectedGroup)}
                                            disabled={generatingKey === selectedGroup.key}
                                            className="uppercase tracking-wider"
                                            data-testid={`generate-invoice-${selectedGroup.key}`}
                                        >
                                            {generatingKey === selectedGroup.key ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    {t('billing.customers.generating_invoice')}
                                                </>
                                            ) : (
                                                <>
                                                    <Send className="w-4 h-4 mr-2" />
                                                    {t('billing.customers.generate_invoice')}
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </div>
                                {renderProjectReports(selectedGroup.reports)}
                            </div>
                        ) : customerGroups.length === 0 ? (
                            <Card className="border border-border shadow-none">
                                <CardContent className="p-8 text-center">
                                    <Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                    <p className="text-muted-foreground">{t('billing.customers.no_billable_customers')}</p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {t('billing.customers.no_billable_customers_hint')}
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card className="border border-border shadow-none">
                                <CardContent className="p-0">
                                    <div className="overflow-x-auto">
                                        <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>{t('billing.customers.col_customer')}</th>
                                                    <th className="text-right">{t('billing.customers.col_projects')}</th>
                                                    <th className="text-right">{t('billing.customers.col_hours')}</th>
                                                    {/* Between hours and amount so the row reads as it
                                                        adds up: hours at the project rate, plus the
                                                        billable expenses and mileage, equals the
                                                        amount — which already contained them but
                                                        never said so (Task 22.8). */}
                                                    <th className="text-right">{t('billing.customers.col_expenses')}</th>
                                                    <th className="text-right">{t('billing.customers.col_amount')}</th>
                                                    <th className="text-right">{t('billing.customers.col_actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {customerGroups.map((group) => (
                                                    <tr key={group.key} data-testid={`customer-row-${group.key}`}>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="font-medium text-left hover:underline"
                                                                onClick={() => setSelectedClientKey(group.key)}
                                                                data-testid={`customer-drilldown-${group.key}`}
                                                            >
                                                                {group.clientName}
                                                            </button>
                                                            {!group.clientId && (
                                                                <Badge variant="secondary" className="ml-2">
                                                                    {t('billing.customers.no_client')}
                                                                </Badge>
                                                            )}
                                                            {group.unresolved && (
                                                                <Badge variant="destructive" className="ml-2">
                                                                    {t('billing.customers.unresolved_badge')}
                                                                </Badge>
                                                            )}
                                                        </td>
                                                        <td className="text-right font-mono">{group.projectCount}</td>
                                                        <td className="text-right font-mono">{formatHours(group.totalHours)}h</td>
                                                        <td
                                                            className="text-right font-mono"
                                                            data-testid={`customer-expenses-${group.key}`}
                                                        >
                                                            {formatSEK(group.totalExpenses)}
                                                        </td>
                                                        <td className="text-right font-mono font-bold text-green-600">
                                                            {formatSEK(group.totalAmount)}
                                                        </td>
                                                        <td className="text-right whitespace-nowrap">
                                                            {group.clientId && !group.unresolved && !selectedProject && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handleGenerateInvoice(group)}
                                                                    disabled={generatingKey === group.key}
                                                                    data-testid={`generate-invoice-${group.key}`}
                                                                >
                                                                    {generatingKey === group.key ? (
                                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                                    ) : (
                                                                        <>
                                                                            <Send className="w-4 h-4 mr-1" />
                                                                            {t('billing.customers.generate_invoice')}
                                                                        </>
                                                                    )}
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>
                </Tabs>
            )}
            {/* Generate-invoice success state (Task 21.10), now handing off to
                the Invoices page (Task 21.11): the draft is where the lines are
                edited and where it is issued, so "Open invoice" is the primary
                action and closing is the secondary one. */}
            <Dialog open={!!generatedInvoice} onOpenChange={(open) => { if (!open) setGeneratedInvoice(null); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {t('billing.customers.invoice_generated_title')}
                        </DialogTitle>
                    </DialogHeader>
                    {generatedInvoice && (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <Badge variant="outline">{t('invoices.status_draft')}</Badge>
                                <span className="text-sm text-muted-foreground">
                                    {t('invoices.generated_from_billing')}
                                </span>
                            </div>
                            <p className="text-sm">
                                {t('billing.customers.invoice_generated_body', {
                                    client: generatedInvoice.clientName,
                                    amount: formatSEK(generatedInvoice.invoice.total_incl_vat),
                                })}
                            </p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setGeneratedInvoice(null)}
                            data-testid="close-invoice-generated-dialog"
                        >
                            {t('common.close')}
                        </Button>
                        {/* Guarded like the body above, and for the same reason: the
                            dialog's content is only meaningful while generatedInvoice
                            exists. Leaving the footer ungated meant the body defended
                            against a null the footer would still have dereferenced —
                            an inconsistency, whichever of the two was the mistake. */}
                        {generatedInvoice && (
                            <Button
                                onClick={() => navigate(`/dashboard/invoices/${generatedInvoice.invoice.id}`)}
                                data-testid="open-generated-invoice"
                            >
                                {t('billing.customers.open_invoice')}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
